import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CheckIcon, DatabaseIcon } from "@/design/icons";
import type { FC } from "react";

import {
  BID_AGENT_DEFAULT_TOOLS,
  CONTENT_LIBRARY_TOOL,
  TENDER_LIBRARY_TOOL,
  WEB_SEARCH_TOOL,
} from "@/agents-v2/components/agents/bid-agent-sources";
import { TooltipIconButton } from "@/agents-v2/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/agents-v2/utils/cn";

/**
 * Which sources the agent may use, the pane's version of the web app's
 * ToolSelector: the same three ids, sent as `selected_tools` on the run
 * context (see bid-agent-sources.ts for the normalisation).
 */
const SOURCES: { id: string; label: string; hint: string }[] = [
  { id: CONTENT_LIBRARY_TOOL, label: "Content library", hint: "Your organisation's past bids and documents" },
  { id: TENDER_LIBRARY_TOOL, label: "Tender documents", hint: "The linked tender's own documents" },
  { id: WEB_SEARCH_TOOL, label: "Internet", hint: "External research" },
];

export const SourcesControl: FC<{
  selectedTools: string[];
  onChange: (next: string[]) => void;
  tenderLinked: boolean;
}> = ({ selectedTools, onChange, tenderLinked }) => {
  const toggle = (id: string) =>
    onChange(selectedTools.includes(id) ? selectedTools.filter((t) => t !== id) : [...selectedTools, id]);
  const allOn = BID_AGENT_DEFAULT_TOOLS.every((id) => selectedTools.includes(id));
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <TooltipIconButton
          tooltip={
            allOn
              ? "Sources: all"
              : `Sources: ${SOURCES.filter((s) => selectedTools.includes(s.id)).length} of ${SOURCES.length}`
          }
          side="top"
          type="button"
          className={cn("size-8 rounded-full", !allOn && "text-accent")}
        >
          <DatabaseIcon className="size-4" />
        </TooltipIconButton>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          align="start"
          sideOffset={6}
          className="z-30 w-64 rounded-md border border-line bg-surface p-1 text-sm text-fg shadow-pop"
        >
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Sources io.ai may use</div>
          {SOURCES.map((source) => {
            const on = selectedTools.includes(source.id);
            const disabled = source.id === TENDER_LIBRARY_TOOL && !tenderLinked;
            return (
              <button
                key={source.id}
                type="button"
                disabled={disabled}
                onClick={() => toggle(source.id)}
                className="flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-subtle disabled:opacity-50"
              >
                <span
                  className={cn(
                    "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border",
                    on ? "border-accent bg-accent text-fg-inverse" : "border-line-strong"
                  )}
                >
                  {on && <CheckIcon className="size-3" />}
                </span>
                <span className="min-w-0">
                  <span className="block">{source.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {disabled ? "Link a tender to search its documents" : source.hint}
                  </span>
                </span>
              </button>
            );
          })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
